import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    Component,
    computed,
    inject,
    model,
    signal,
} from "@angular/core";
import {
    CreateScheduleLogDto,
    DocumentAiService,
    EnrollmentScheduleDto,
    ScheduleLogService,
} from "@malirang/api-client";
import {
    Badge,
    Button,
    FileUploader,
    Label,
    ModalAdapter,
    ModalContainer,
    ModalHeader,
    Textarea,
    ToastService,
} from "@malirang/front-share";
import { CenterStore } from "../../../../../../stores/center/center.store";
import { DocumentStore } from "../../../store/document.store";
import { UploadService } from "../../../../../../services/upload.service";
import { Field, form, required } from "@angular/forms/signals";

export type ScheduleLogFormModalProps = {
    enrollmentSchedule: EnrollmentScheduleDto;
};

@Component({
    selector: "app-schedule-log-form",
    templateUrl: "./schedule-log-form.modal.html",
    styleUrls: ["./schedule-log-form.modal.css"],
    imports: [
        CommonModule,
        ModalContainer,
        ModalHeader,
        Button,
        Textarea,
        Badge,
        Label,
        FileUploader,
        Field,
    ],
})
export class ScheduleLogFormModal extends ModalAdapter<ScheduleLogFormModalProps> {
    readonly centerStore = inject(CenterStore);
    readonly scheduleLogService = inject(ScheduleLogService);
    readonly documentStore = inject(DocumentStore);
    readonly uploadService = inject(UploadService);
    readonly documentAiService = inject(DocumentAiService);
    readonly toast = inject(ToastService);

    upload = this.uploadService.upload.bind(this);
    delete = this.uploadService.delete.bind(this);

    editRole = computed(() => this.documentStore.editRole());
    schedule = computed(() => this.props()?.enrollmentSchedule);

    formModel = signal<CreateScheduleLogDto>({
        content: "",
        counselingContent: "",
        files: [],
        specialContent: "",
    });
    form = form(this.formModel, (schemaPath) => {
        required(schemaPath.content);
    });

    aiLoading = signal(false);

    constructor() {
        super();

        afterNextRender(() => {
            const log = this.schedule()?.log;
            if (log) {
                this.form().setControlValue({
                    content: log.content,
                    counselingContent: log.counselingContent,
                    files: log.files?.map((file) => file.url) || [],
                    specialContent: log.specialContent,
                });
            }
        });
    }

    aiEnhance() {
        if (!this.formModel().content) {
            this.toast.error("회기 기록을 입력해주세요.");
            return;
        }

        this.aiLoading.set(true);
        this.documentAiService
            .documentAiControllerAiEnhancement({
                body: {
                    title: "회기 기록",
                    content: this.formModel().content,
                    transformTitle: "전문적인 회기 기록",
                    additionalPrompt: "",
                },
            })
            .subscribe({
                next: (res) => {
                    this.form().setControlValue({
                        content: res.content,
                        counselingContent: this.formModel().counselingContent,
                        files: this.formModel().files,
                        specialContent: this.formModel().specialContent,
                    });
                    this.toast.success("회기 기록이 보정되었습니다.");
                    this.aiLoading.set(false);
                },
                error: () => {
                    this.toast.error("AI 보정에 실패했습니다.");
                    this.aiLoading.set(false);
                },
            });
    }

    submit() {
        if (!this.editRole()) {
            this.toast.error("회기 일지 수정 권한이 없습니다.");
            return;
        }

        if (this.schedule()?.log) {
            this.scheduleLogService
                .scheduleLogControllerUpdate({
                    body: this.formModel(),
                    centerId: this.centerStore.currentCenterId()!,
                    id: this.schedule()!.log!.id,
                })
                .subscribe({
                    next: () => {
                        this.toast.success("회기 일지가 수정되었습니다.");
                        this.close(true);
                    },
                });
        } else {
            this.scheduleLogService
                .scheduleLogControllerCreate({
                    body: this.formModel(),
                    centerId: this.centerStore.currentCenterId()!,
                    enrollmentScheduleId: this.schedule()!.id,
                })
                .subscribe({
                    next: () => {
                        this.toast.success("회기 일지가 작성되었습니다.");
                        this.close(true);
                    },
                });
        }
    }
}
